<template>
  <div
    ref="scroller"
    class="h-100 overflow-y-auto styled-scrollbar"
    @scroll.passive="onScroll"
  >
    <div
      class="d-flex flex-column ga-6 pa-6"
      role="log"
      :aria-busy="bekkiChatStore.isStreaming"
      data-cc="bekki-messages"
    >
      <template v-for="message in visibleMessages" :key="message.id">
        <BekkiUserMessage v-if="message.role === 'user'" :text="message.text" />
        <BekkiAssistantMessage
          v-else
          :message="message"
          :isStreaming="message.id === bekkiChatStore.activeMessageId"
          :isRetryable="message.id === lastMessageId"
          :steps="
            message.id === bekkiChatStore.activeMessageId
              ? bekkiChatStore.pendingSteps
              : []
          "
          @retry="bekkiChatStore.retryLast"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import BekkiAssistantMessage from '@/components/bekki/BekkiAssistantMessage.vue';
import BekkiUserMessage from '@/components/bekki/BekkiUserMessage.vue';

import type { ChatMessage } from '@/composables/api/bekki/types';
import useBekkiChatStore, { hasMessageContent } from '@/stores/bekkiChat';

const FOLLOW_THRESHOLD_PX = 48;

const bekkiChatStore = useBekkiChatStore();

const scroller = ref<HTMLElement | null>(null);
const isFollowing = ref(true);

const lastMessageId = computed(
  (): string | null =>
    bekkiChatStore.messages[bekkiChatStore.messages.length - 1]?.id ?? null
);

const visibleMessages = computed((): ChatMessage[] =>
  bekkiChatStore.messages.filter(
    (message) =>
      message.role === 'user' ||
      hasMessageContent(message) ||
      message.error !== undefined ||
      message.id === bekkiChatStore.activeMessageId
  )
);

const onScroll = (): void => {
  const element = scroller.value;
  if (!element) return;
  isFollowing.value =
    element.scrollHeight - element.scrollTop - element.clientHeight <=
    FOLLOW_THRESHOLD_PX;
};

const scrollToBottom = (): void => {
  const element = scroller.value;
  if (element) element.scrollTop = element.scrollHeight;
};

watch(
  () => bekkiChatStore.messages.length,
  () => {
    isFollowing.value = true;
  }
);

watch(
  [() => bekkiChatStore.messages, () => bekkiChatStore.pendingSteps],
  async () => {
    if (!isFollowing.value) return;
    await nextTick();
    scrollToBottom();
  },
  { deep: true }
);

onMounted(scrollToBottom);
</script>
